import React, { useContext } from 'react';

import { ListerContext } from '../../listerContext';
import { MessagePropertiesContext } from '../../../../contexts/messageProperties';
import useListerApi from '../../listerApiHooks';
import PaginationCTA from './paginationCta';
import styles from './pagination.module.pcss';
import { updatePageURLParam, useSyncUrlWithClickedProductPage } from './productClickHooks';
import useStickyProductsPrepend from './paginationScrollHook';
import useVisiblePages from './visiblePagesHook';

interface PaginationContainerProps {
  children: React.ReactNode;
}

function PaginatedResults({ children }: PaginationContainerProps): React.ReactElement {
  const [formattedMessage] = useContext(MessagePropertiesContext);
  const [{ isListerDisabled, pagination, results }] = useContext(ListerContext);
  const { showNextProducts, showPreviousProducts } = useListerApi();
  const { currentPage, numberOfPages, totalNumberOfResults, pageSize } = pagination;
  const { firstVisiblePage, lastVisiblePage, setFirstVisiblePage, setLastVisiblePage } = useVisiblePages();
  const hasMore = numberOfPages > lastVisiblePage;
  const hasPrev = firstVisiblePage > 1 && currentPage !== 1;

  useStickyProductsPrepend(results[0]?.code, currentPage === 1);
  useSyncUrlWithClickedProductPage({ pageOffset: firstVisiblePage, pageSize });

  const showPreviousClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    event.preventDefault();
    window.globalLister.scrollYToFirstProduct = window.pageYOffset;
    const previousPage = firstVisiblePage - 1;
    showPreviousProducts({ page: previousPage }).then(() => {
      setFirstVisiblePage(previousPage);
      updatePageURLParam(previousPage);
    });
  };

  const showMoreClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    event.preventDefault();
    const nextPage = lastVisiblePage + 1;

    showNextProducts({ page: nextPage }).then(() => {
      setLastVisiblePage(nextPage);
      updatePageURLParam(nextPage);
    });
  };

  return (
    <>
      {hasPrev && (
        <div className={styles.pagination}>
          <PaginationCTA
            clickHandler={showPreviousClickHandler}
            page={firstVisiblePage - 1}
            rel="prev"
            isDisabled={isListerDisabled}
          >
            {formattedMessage({ id: 'text.label.showprevresults', fallback: 'Show Previous Results' })}
          </PaginationCTA>
        </div>
      )}
      {children}
      <div className={styles.pagination}>
        <span className={styles.pagination__info}>
          {formattedMessage({
            id: 'text.label.searchresults',
            fallback: '0/0 Results',
            values: {
              0: Math.min(lastVisiblePage * pageSize, totalNumberOfResults),
              1: totalNumberOfResults
            }
          })}
        </span>
        {hasMore && (
          <PaginationCTA
            clickHandler={showMoreClickHandler}
            page={lastVisiblePage + 1}
            rel="next"
            isDisabled={isListerDisabled}
          >
            {formattedMessage({ id: 'text.label.showmoreresults', fallback: 'Show More Results' })}
          </PaginationCTA>
        )}
      </div>
    </>
  );
}

export default PaginatedResults;
